import { compareContentRowsByOrder } from '../utils/scheduleContentMatcher';
import React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { trackNameFromProgramPath } from '../utils/programRouteParams';
import { useProgramProgressSavedRefresh } from '../hooks/useProgramProgressSavedRefresh';
import { useProgramPostSaveDisplayHold, cloneProgramTableHoldPayload } from '../hooks/useProgramPostSaveDisplayHold';
import { useReleaseTrappedKeepAliveFocusWhenInactive } from '../utils/keepAliveFocusRelease';
import { GradeTrackLeaveContext } from '../contexts/GradeTrackLeaveContext';
import { useProgramSidebar } from '../contexts/ProgramSidebarContext';
import { useKeepAliveProgramTerm } from '../hooks/useKeepAliveProgramTerm';
import { useCalendarLinkedAcademicYear } from '../hooks/useCalendarLinkedAcademicYear';
import { ProgramProgressEmptyStateTableCard } from '../components/program/ProgramProgressEmptyStateTableCard';
import ProgramProgressTable, { type ProgramProgressUndoRedoApi } from '../components/program/ProgramProgressTable';
import ProgramImportLearningContentModal from '../components/program/ProgramImportLearningContentModal';
import ProgramExportLearningContentModal from '../components/program/ProgramExportLearningContentModal';
import ProgramLearningContentActionSelectModal from '../components/program/ProgramLearningContentActionSelectModal';
import ProgramTableDocumentExportButtons from '../components/program/ProgramTableDocumentExportButtons';
import ProgramImportLearningDisabledHint from '../components/program/ProgramImportLearningDisabledHint';
import ProgramWorkspaceStaleConflictBanner from '../components/program/ProgramWorkspaceStaleConflictBanner';
import ProgramToolbarUndoRedoButtons from '../components/program/ProgramToolbarUndoRedoButtons';
import { useProgramProgressData } from '../hooks/useProgramProgressData';
import { useProgramScopedContentRows } from '../hooks/useProgramScopedContentRows';
import { useProgramWorkspaceScopeServerSync } from '../hooks/useProgramWorkspaceScopeServerSync';
import { useAnnualProjectProgramsResolved } from '../hooks/useAnnualProjectProgramsResolved';
import {
  getGradeTrackProgramPreferences,
  loadGradeTrackProgramPreferencesCache,
} from '../services/sectionLayoutService';
import { applyGradeTrackProgramPrefsToLocalStores } from '../utils/gradeTrackProgramPrefsLocalSeed';
import { readProgramLearningContentFields, writeProgramLearningContentFields } from '../utils/programLearningContentFields';
import { ANNUAL_PREREQUISITE_REFRESH_EVENT } from '../utils/annualPrerequisiteRefresh';
import { parseGradeDisplayName } from '../utils/gradeDisplayName';
import {
  AdminLearningContentRow,
  ProgramFixedContentRowUpsert,
  ProgramProgressCell,
  ProgramScopedContentRow,
} from '../types/domain';
import {
  fetchProgramScopedContentRowsHead,
  getProgramScopedContentRows,
  saveProgramScopedContentRowsDiff,
} from '../services/programScopedContentService';
import { notifySchoolProgramMutation } from '../utils/schoolDataCacheVersion';
import {
  buildProgramProgressRows,
  buildTrackNameByTimetableTextByGrade,
  buildWeeksFromRange,
  resolveClassEntryByGradeId,
} from '../utils/programProgressRows';
import {
  ensureProgramWorkspaceScopeSnapshot,
  isAnyProgramWorkspaceScopeDirty,
  getProgramWorkspaceScopeEntry,
  PROGRAM_WORKSPACE_CHANGED_EVENT,
  pruneProgramWorkspaceScopes,
  registerDiscardDirtyWorkspaceOnPageHide,
  removeProgramWorkspaceScopeEntry,
  resolveProgramWorkspacePersistKind,
  upsertProgramWorkspaceScopeEntry,
  type ProgramWorkspaceRowsChangeMeta,
  type ProgramWorkspaceScopeEntry,
} from '../utils/programEditWorkspace';
import {
  computeProgramSavePending,
  discardAllProgramFamilyLeaveDrafts,
  saveProgramGradeTermWorkspaceAndAttachments,
} from '../utils/programGradeTermSave';
import {
  PROGRAM_CONTENT_SLIDE_DRAFT_PREFIX,
  PROGRAM_CONTENT_SLIDE_DRAFTS_CHANGED_EVENT,
} from '../services/programContentSlideService';
import {
  LEARNING_WORKSHEET_PENDING_PREFIX,
  LEARNING_WORKSHEET_PENDING_CHANGED_EVENT,
} from '../utils/learningContentWorksheetPendingStorage';
import { mergeProgramWorkspaceUpsertsIntoBaselineRows } from '../utils/programWorkspaceDisplayMerge';
import {
  alignProgramContentRowsToScheduleSlotLayout,
  buildTrackFixedWorkspaceBaselineUpserts,
  isPristineMinimumProgramContentPlaceholderRows,
  programFixedWorkspaceRowsDigest,
  trimTrailingEmptyProgramContentOverflowRows,
  withMinimumProgramContentRowsForDisplay,
} from '../utils/programWorkspaceBaselineDigest';
import { loadTrackAliasOrderMap } from '../utils/trackAliasOrderStorage';
import { assembleTrackScopedRowsForSchedule } from '../utils/trackScheduleContentRematch';
import {
  buildGradeTrackTimetableContext,
  normalizeTimetableTextToDefaultAlias,
} from '../utils/trackTimetableAlias';
import {
  mergeProgramBaselineWithAdminLearningContent,
  exportProgramLearningToWorkspace,
  programAlignedImportColumnsHaveData,
  PROGRAM_IMPORT_LEARNING_CONTENT_BLOCKED_MESSAGE,
} from '../utils/programLearningContentImport';
import { resolveProgramImportLearningDisabledHint } from '../utils/programImportLearningDisabledHint';
import {
  createProgramLearningContentCsv,
  parseProgramLearningContentCsv,
} from '../utils/programLearningContentCsv';
import {
  loadTrackProgramModeMap,
  saveTrackProgramModeMap,
} from '../utils/trackProgramModeStorage';
import { loadTrackProgramSyncRepMap, saveTrackProgramSyncRepMap } from '../utils/trackProgramSyncRepStorage';
import { TRACK_PROGRAM_SPLIT_CHANGE_EVENT } from '../utils/trackProgramSplitStorage';
import { buildWildcardCharsByGrade } from '../utils/trackWildcard';
import { readValidScopedContentRowsFromLocalCache, sliceScopedRowsFromGradeTermLocalCache } from '../utils/programScopedContentLsBridge';
import { pickProgramScopedRowsForDisplay } from '../utils/programScopedRowsDisplayPick';
import { preserveIndependentScopedRecordFieldsOnFanout, overlaySyncMirrorProgramContentFromRepresentative } from '../utils/trackSyncScopedContent';
import {
  buildProgramDocumentScopeLabel,
  buildProgramLearningContentExportFileBase,
  buildProgramTrackDocumentTitle,
  resolveProgramExportTimetableName,
  useProgramLearningContentDocumentExport,
} from '../hooks/useProgramLearningContentDocumentExport';
import {
  buildProgramExportTrackTimetableParts,
} from '../utils/programExportDocumentTitle';

const MIRROR_LOCKED_FIELD_IDS = [
  'timetable_text',
  'unit_text',
  'learning_text',
  'page_text',
  'assistant_page_text',
  'current_period_text',
  'total_period_text',
  'material_text',
  'record_cross_curricular_text',
  'record_memo_text',
  'record_evaluation_text',
];

type ProgramTrackPageProps = {
  isActive?: boolean;
};

/** 편제별 프로그램 — 해당 편제의 진도표 일정만 표시(고정·프로젝트 제외) */
const ProgramTrackPage: React.FC<ProgramTrackPageProps> = ({ isActive = true }) => {
  const location = useLocation();
  useReleaseTrappedKeepAliveFocusWhenInactive(isActive);
  const [searchParams] = useSearchParams();
  const lastActiveTrackRef = React.useRef('');
  const decodedTrack = React.useMemo(() => {
    if (!isActive) return lastActiveTrackRef.current;
    const fromPath = trackNameFromProgramPath(location.pathname);
    if (fromPath) lastActiveTrackRef.current = fromPath;
    return fromPath || lastActiveTrackRef.current;
  }, [isActive, location.pathname]);
  const { programFocusGradeId, grades, workspaceId, setProgramFocusGradeId } = useProgramSidebar();

  const gradeIdFromUrl = searchParams.get('gradeId');
  const focusAnnualCellKeyFromUrl = searchParams.get('focusAnnualCellKey')?.trim() ?? '';
  const programTermFromUrl = searchParams.get('programTerm');
  const timetableNameFromUrl = searchParams.get('programTimetableName')?.trim() ?? '';
  const effectiveGradeId = gradeIdFromUrl || programFocusGradeId || '';
  const termNumber = useKeepAliveProgramTerm(isActive, programTermFromUrl);
  const academicYear = useCalendarLinkedAcademicYear(workspaceId, effectiveGradeId.trim());

  const gradeRow = React.useMemo(
    () => (effectiveGradeId ? grades.find((g) => g.id === effectiveGradeId) ?? null : null),
    [grades, effectiveGradeId]
  );
  const classParsed = React.useMemo(
    () => (gradeRow ? parseGradeDisplayName(gradeRow.name) : null),
    [gradeRow]
  );
  const defaultGradeNumberForImport = React.useMemo((): 1 | 2 | 3 | 4 | 5 | 6 => {
    const g = classParsed?.grades?.[0];
    if (typeof g === 'number' && g >= 1 && g <= 6) return g as 1 | 2 | 3 | 4 | 5 | 6;
    return 1;
  }, [classParsed]);

  React.useEffect(() => {
    if (effectiveGradeId) setProgramFocusGradeId(effectiveGradeId);
  }, [effectiveGradeId, setProgramFocusGradeId]);

  const {
    loading,
    error,
    terms,
    schedule,
    tracks,
    gradeTrackSections,
    regularColors,
    dedicatedColors,
    baseExcludeColors,
    holidaySet,
    cellsByKey,
    schoolScopedCacheSyncReady,
  } = useProgramProgressData({
    workspaceId,
    grades,
    gradeId: effectiveGradeId,
    academicYear,
    termNumber: termNumber ?? 1,
    isActive,
  });
  const dataActive = isActive && schoolScopedCacheSyncReady;
  const selectedEntry = React.useMemo(
    () => resolveClassEntryByGradeId(schedule, grades, effectiveGradeId),
    [effectiveGradeId, grades, schedule]
  );
  const termDef = React.useMemo(
    () => (termNumber ? terms.find((term) => term.term_number === termNumber) ?? null : null),
    [termNumber, terms]
  );
  const weeks = React.useMemo(
    () => buildWeeksFromRange(termDef?.starts_on ?? '', termDef?.ends_on ?? ''),
    [termDef?.starts_on, termDef?.ends_on]
  );
  const trackNameByTimetableTextByGrade = React.useMemo(
    () => buildTrackNameByTimetableTextByGrade(tracks),
    [tracks]
  );
  const gradeTrackTimetableContext = React.useMemo(
    () => buildGradeTrackTimetableContext(workspaceId, effectiveGradeId, tracks, gradeTrackSections),
    [workspaceId, effectiveGradeId, tracks, gradeTrackSections]
  );
  const registeredTimetableNames = React.useMemo(() => {
    const set = new Set<string>();
    tracks.forEach((track) => {
      if ((track.grade_id ?? '').trim() !== effectiveGradeId) return;
      const base = (track.name ?? '').trim();
      const alias = (track.description ?? '').trim();
      if (base) set.add(base);
      if (alias) set.add(alias);
    });
    return Array.from(set);
  }, [effectiveGradeId, tracks]);
  const registeredTimetableWildcardChars = React.useMemo(() => {
    const byGrade = buildWildcardCharsByGrade(gradeTrackSections);
    return Array.from(byGrade[effectiveGradeId] ?? []);
  }, [effectiveGradeId, gradeTrackSections]);
  const annualProjectPrograms = useAnnualProjectProgramsResolved(workspaceId, effectiveGradeId, academicYear);

  const programTrackAliasesOrdered = React.useMemo(() => {
    if (!decodedTrack || !effectiveGradeId) return [] as string[];
    const aliases = Array.from(
      new Set(
        tracks
          .filter((t) => (t.grade_id ?? '') === effectiveGradeId && (t.name ?? '').trim() === decodedTrack)
          .map((t) => (t.description ?? '').trim())
          .filter(Boolean)
      )
    );
    const stored = loadTrackAliasOrderMap(workspaceId, effectiveGradeId)[decodedTrack] ?? [];
    if (!stored.length) return aliases;
    return [
      ...stored.filter((a) => aliases.includes(a)),
      ...aliases.filter((a) => !stored.includes(a)),
    ];
  }, [tracks, effectiveGradeId, decodedTrack, workspaceId]);

  const [programPrefsRev, setProgramPrefsRev] = React.useState(0);

  const syncProgramPrefsToLocalStores = React.useCallback(() => {
    if (!workspaceId || !effectiveGradeId) return;
    const cached = loadGradeTrackProgramPreferencesCache(workspaceId);
    if (cached && applyGradeTrackProgramPrefsToLocalStores(workspaceId, effectiveGradeId, cached)) {
      setProgramPrefsRev((r) => r + 1);
    }
  }, [effectiveGradeId, workspaceId]);

  React.useEffect(() => {
    if (!isActive || !workspaceId || !effectiveGradeId) return;
    syncProgramPrefsToLocalStores();
    let cancelled = false;
    void getGradeTrackProgramPreferences(workspaceId)
      .then((prefs) => {
        if (cancelled) return;
        if (applyGradeTrackProgramPrefsToLocalStores(workspaceId, effectiveGradeId, prefs)) {
          setProgramPrefsRev((r) => r + 1);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [effectiveGradeId, isActive, syncProgramPrefsToLocalStores, workspaceId]);

  React.useEffect(() => {
    /** LS mode/sync 맵이 이미 갱신된 뒤 알림 — 재기록하면 save→이벤트→재기록 무한 루프 */
    const onTrackProgramSplitChanged = () => {
      setProgramPrefsRev((r) => r + 1);
    };
    const onAnnualPrerequisiteRefresh = () => {
      syncProgramPrefsToLocalStores();
    };
    window.addEventListener(TRACK_PROGRAM_SPLIT_CHANGE_EVENT, onTrackProgramSplitChanged);
    window.addEventListener(ANNUAL_PREREQUISITE_REFRESH_EVENT, onAnnualPrerequisiteRefresh);
    return () => {
      window.removeEventListener(TRACK_PROGRAM_SPLIT_CHANGE_EVENT, onTrackProgramSplitChanged);
      window.removeEventListener(ANNUAL_PREREQUISITE_REFRESH_EVENT, onAnnualPrerequisiteRefresh);
    };
  }, [syncProgramPrefsToLocalStores]);

  const trackProgramMode = React.useMemo(() => {
    void programPrefsRev;
    if (!effectiveGradeId || !decodedTrack) return 'unified' as const;
    const m = loadTrackProgramModeMap(workspaceId, effectiveGradeId)[decodedTrack];
    return m === 'split' || m === 'sync' || m === 'unified' ? m : 'unified';
  }, [workspaceId, effectiveGradeId, decodedTrack, programPrefsRev]);

  const syncRepAlias = React.useMemo(() => {
    void programPrefsRev;
    if (!effectiveGradeId || !decodedTrack) return '';
    const repMap = loadTrackProgramSyncRepMap(workspaceId, effectiveGradeId);
    const ordered = programTrackAliasesOrdered;
    if (ordered.length < 2) return ordered[0] ?? '';
    const rep = repMap[decodedTrack]?.trim() ?? '';
    if (rep && ordered.includes(rep)) return rep;
    return ordered[0] ?? '';
  }, [workspaceId, effectiveGradeId, decodedTrack, programTrackAliasesOrdered, programPrefsRev]);

  const isSyncActive = trackProgramMode === 'sync' && programTrackAliasesOrdered.length >= 2;

  const timetableFilter = React.useMemo(() => {
    if (trackProgramMode === 'unified') return '';
    if (timetableNameFromUrl) return timetableNameFromUrl;
    if (isSyncActive) return syncRepAlias;
    return '';
  }, [trackProgramMode, timetableNameFromUrl, isSyncActive, syncRepAlias]);
  const scopeKey = React.useMemo(() => {
    const trackPart = decodedTrack || '-';
    return `${trackPart}|${timetableFilter || '-'}`;
  }, [decodedTrack, timetableFilter]);
  const displayTimetableName = trackProgramMode === 'unified' ? '' : timetableNameFromUrl;

  const isSyncMirrorView =
    isSyncActive &&
    !!displayTimetableName &&
    displayTimetableName !== syncRepAlias;
  const [workspaceStateRev, setWorkspaceStateRev] = React.useState(0);
  const [saveRefreshRev, setSaveRefreshRev] = React.useState(0);
  const bumpSaveRefreshRev = React.useCallback(() => setSaveRefreshRev((v) => v + 1), []);
  const bumpProgramSavePendingRevs = React.useCallback(() => {
    bumpSaveRefreshRev();
    setSlideDraftDirtyRev((v) => v + 1);
    setWorksheetDraftDirtyRev((v) => v + 1);
    setWorkspaceStateRev((v) => v + 1);
  }, [bumpSaveRefreshRev]);
  useProgramProgressSavedRefresh({
    workspaceId,
    gradeId: effectiveGradeId,
    academicYear,
    termNumber,
    onRefresh: bumpProgramSavePendingRevs,
  });
  /** 저장 없이 이탈 시 표를 초기 상태로 다시 마운트 */
  const [programTableMountKey, setProgramTableMountKey] = React.useState(0);
  const [importLearningModalOpen, setImportLearningModalOpen] = React.useState(false);
  const [exportLearningModalOpen, setExportLearningModalOpen] = React.useState(false);
  const [importMethodSelectOpen, setImportMethodSelectOpen] = React.useState(false);
  const [exportMethodSelectOpen, setExportMethodSelectOpen] = React.useState(false);
  const [importLearningApplying, setImportLearningApplying] = React.useState(false);
  const [exportLearningApplying, setExportLearningApplying] = React.useState(false);
  const importCsvInputRef = React.useRef<HTMLInputElement | null>(null);
  const programUndoRedoApiRef = React.useRef<ProgramProgressUndoRedoApi | null>(null);
  const [programUndoRedoAvail, setProgramUndoRedoAvail] = React.useState({ canUndo: false, canRedo: false });
  void workspaceStateRev;

  /** 1↔2학기 전환 시 표·워크스페이스 병합 메모가 LS와 어긋나지 않도록 리마운트·워크스페이스 구독 무효화 */
  const prevProgramTermRef = React.useRef<1 | 2 | null>(null);
  React.useEffect(() => {
    if (termNumber == null) {
      prevProgramTermRef.current = null;
      return;
    }
    if (prevProgramTermRef.current != null && prevProgramTermRef.current !== termNumber) {
      setProgramTableMountKey((k) => k + 1);
      setWorkspaceStateRev((v) => v + 1);
    }
    prevProgramTermRef.current = termNumber;
  }, [termNumber]);

  const trackWorkspaceScopeId = React.useMemo(
    () => `track|${workspaceId}|${effectiveGradeId}|${academicYear}|${termNumber}|${scopeKey}`,
    [academicYear, effectiveGradeId, workspaceId, termNumber, scopeKey]
  );
  const activeWorkspaceScopeId = React.useMemo(
    () => (workspaceId && effectiveGradeId && termNumber && scopeKey ? trackWorkspaceScopeId : ''),
    [effectiveGradeId, workspaceId, termNumber, trackWorkspaceScopeId, scopeKey]
  );
  const trackWorkspaceEntry = getProgramWorkspaceScopeEntry(trackWorkspaceScopeId);
  const representativeWorkspaceScopeId = React.useMemo(() => {
    if (!workspaceId || !effectiveGradeId || !termNumber || !decodedTrack || !syncRepAlias) return '';
    return `track|${workspaceId}|${effectiveGradeId}|${academicYear}|${termNumber}|${decodedTrack}|${syncRepAlias}`;
  }, [academicYear, decodedTrack, effectiveGradeId, workspaceId, syncRepAlias, termNumber]);
  const representativeTrackWorkspaceEntry = representativeWorkspaceScopeId
    ? getProgramWorkspaceScopeEntry(representativeWorkspaceScopeId)
    : null;
  const representativeScopeKey = React.useMemo(() => {
    if (!decodedTrack || !syncRepAlias) return '';
    return `${decodedTrack}|${syncRepAlias}`;
  }, [decodedTrack, syncRepAlias]);
  const {
    loading: scopedRowsLoading,
    rows: scopedContentRows,
    saveSnapshotRows: saveScopedSnapshotRows,
  } =
    useProgramScopedContentRows({
      workspaceId,
      academicYear,
      termNumber,
      gradeId: effectiveGradeId,
      scopeType: 'track',
      scopeKey,
      refreshKey: saveRefreshRev,
      isActive: dataActive,
    });
  const { rows: representativeScopedContentRows } = useProgramScopedContentRows({
    workspaceId,
    academicYear,
    termNumber,
    gradeId: effectiveGradeId,
    scopeType: 'track',
    scopeKey: representativeScopeKey,
    refreshKey: saveRefreshRev,
    isActive: dataActive && isSyncMirrorView && !!representativeScopeKey,
  });
  const representativeScopedRowsFromCache = React.useMemo(() => {
    if (!isSyncMirrorView || !representativeScopeKey || !workspaceId || !effectiveGradeId || !termNumber) return [];
    const fromScopeCache = readValidScopedContentRowsFromLocalCache(
      workspaceId,
      academicYear,
      termNumber,
      effectiveGradeId,
      'track',
      representativeScopeKey
    ).rows;
    if (fromScopeCache.length) return fromScopeCache;
    return sliceScopedRowsFromGradeTermLocalCache(
      workspaceId,
      academicYear,
      termNumber,
      effectiveGradeId,
      'track',
      representativeScopeKey
    );
  }, [
    academicYear,
    effectiveGradeId,
    isSyncMirrorView,
    representativeScopeKey,
    saveRefreshRev,
    termNumber,
    workspaceId,
    workspaceStateRev,
  ]);
  const scopedRowsFromCache = React.useMemo(() => {
    if (!scopeKey || !workspaceId || !effectiveGradeId || !termNumber) return [];
    const fromScopeCache = readValidScopedContentRowsFromLocalCache(
      workspaceId,
      academicYear,
      termNumber,
      effectiveGradeId,
      'track',
      scopeKey
    ).rows;
    if (fromScopeCache.length) return fromScopeCache;
    return sliceScopedRowsFromGradeTermLocalCache(
      workspaceId,
      academicYear,
      termNumber,
      effectiveGradeId,
      'track',
      scopeKey
    );
  }, [
    academicYear,
    effectiveGradeId,
    saveRefreshRev,
    scopeKey,
    termNumber,
    workspaceId,
    workspaceStateRev,
  ]);

  const rows = React.useMemo(() => {
    if (!selectedEntry || !termDef || !termNumber || !decodedTrack) return [];
    const gradeRawName = grades.find((g) => g.id === selectedEntry.gradeId)?.name ?? selectedEntry.label;
    return buildProgramProgressRows({
      workspaceId,
      academicYear,
      termNumber,
      selectedEntry,
      gradeRawName,
      termDef,
      weeks,
      holidaySet,
      cellsByKey,
      dedicatedColors,
      baseExcludeColors,
      regularColors,
      annualProjectPrograms,
      trackNameByTimetableTextByGrade,
      gradeTrackTimetableContext,
      rowFilter: (input) => {
        const { templateLabel, isFixedProgram, isProjectProgram } = input;
        if (isFixedProgram || isProjectProgram) return false;
        /** 연간시간표에 지금 잡혀 있는 편제만 일정으로 쓴다. 예전 칸 키로 다른 편제를 끌어오지 않는다. */
        return templateLabel === decodedTrack;
      },
    });
  }, [
    academicYear,
    annualProjectPrograms,
    cellsByKey,
    decodedTrack,
    dedicatedColors,
    baseExcludeColors,
    grades,
    holidaySet,
    regularColors,
    workspaceId,
    selectedEntry,
    termDef,
    termNumber,
    trackNameByTimetableTextByGrade,
    gradeTrackTimetableContext,
    weeks,
  ]);
  const normalizeRowTimetableAlias = React.useCallback(
    (row: { timetableText: string; templateLabel: string }) => {
      const raw = (row.timetableText ?? '').trim();
      if (!raw) return '';
      if (!gradeTrackTimetableContext) return raw;
      return normalizeTimetableTextToDefaultAlias(raw, gradeTrackTimetableContext, row.templateLabel);
    },
    [gradeTrackTimetableContext]
  );

  const filteredRows = React.useMemo(
    () =>
      timetableFilter
        ? rows.filter((row) => normalizeRowTimetableAlias(row) === timetableFilter)
        : rows,
    [normalizeRowTimetableAlias, rows, timetableFilter]
  );
  const representativeScheduleAnnualKeys = React.useMemo(
    () =>
      syncRepAlias
        ? rows
            .filter((row) => normalizeRowTimetableAlias(row) === syncRepAlias)
            .map((row) => row.annualCellKey)
        : [],
    [normalizeRowTimetableAlias, rows, syncRepAlias]
  );
  const scheduleSlotAnnualCellKeys = React.useMemo(
    () => filteredRows.map((r) => r.annualCellKey),
    [filteredRows]
  );
  /** 훅이 아직 비어 있어도 저장 직후 LS에 쓴 본문을 바로 보여 빈 표를 피한다. */
  const rowsForDisplayScope = React.useMemo(
    () => pickProgramScopedRowsForDisplay(scopedContentRows, scopedRowsFromCache),
    [scopedContentRows, scopedRowsFromCache]
  );
  /** 스코프 행 순서를 유지한 채 현재 연간 일정과 짝을 맞춘다(남는 내용만 넘침). */
  const scopedRowsForSchedulePairing = React.useMemo(() => {
    if (!workspaceId || !effectiveGradeId || !termNumber || !scopeKey) return rowsForDisplayScope;
    return assembleTrackScopedRowsForSchedule(
      rowsForDisplayScope,
      filteredRows.map((row) => row.annualCellKey),
      {
        workspaceId,
        gradeId: effectiveGradeId,
        academicYear,
        termNumber,
        scopeKey,
      }
    );
  }, [
    academicYear,
    effectiveGradeId,
    filteredRows,
    rowsForDisplayScope,
    scopeKey,
    termNumber,
    workspaceId,
  ]);
  const baselineWorkspaceUpserts = React.useMemo((): ProgramFixedContentRowUpsert[] => {
    if (!effectiveGradeId || !termNumber || !workspaceId) return [];
    const scopedSorted = [...scopedRowsForSchedulePairing].sort(compareContentRowsByOrder);
    return buildTrackFixedWorkspaceBaselineUpserts({
      workspaceId,
      academicYear,
      termNumber,
      gradeId: effectiveGradeId,
      filteredRows,
      scopedRowsSorted: scopedSorted,
    });
  }, [academicYear, effectiveGradeId, filteredRows, scopedRowsForSchedulePairing, workspaceId, termNumber]);
  /** 저장 후 dirty 비교: 일정 슬롯만이 아니라 넘침 행까지 포함한 길이·내용으로 맞춘다(scoped DB만 사용). */
  const persistedAlignedWorkspaceUpserts = React.useMemo((): ProgramFixedContentRowUpsert[] => {
    if (!effectiveGradeId || !termNumber || !workspaceId) return [];
    const scopedSorted = [...scopedRowsForSchedulePairing].sort(compareContentRowsByOrder);
    return alignProgramContentRowsToScheduleSlotLayout({
      contentRowsSorted: scopedSorted,
      baselineRows: baselineWorkspaceUpserts,
    });
  }, [baselineWorkspaceUpserts, effectiveGradeId, scopedRowsForSchedulePairing, workspaceId, termNumber]);
  const tableLoading = loading || scopedRowsLoading;
  const representativeEffectiveScopedRowsForIndicators = React.useMemo(() => {
    const sourceRows = representativeScopedContentRows.length
      ? representativeScopedContentRows
      : representativeScopedRowsFromCache;
    if (!sourceRows.length) return [];
    const baselineSorted = [...sourceRows].sort(compareContentRowsByOrder);
    if (representativeTrackWorkspaceEntry?.isDirty && representativeTrackWorkspaceEntry.rows.length) {
      const merged = mergeProgramWorkspaceUpsertsIntoBaselineRows(
        baselineSorted,
        representativeTrackWorkspaceEntry.rows,
        {
          persistKind:
            representativeTrackWorkspaceEntry.persistKind === 'replace' ? 'replace' : 'patch',
          identity: 'id-position',
        }
      );
      return merged.map((row, idx) => ({
        ...row,
        id: row.id ?? `virtual-${representativeWorkspaceScopeId}-${idx}`,
      }));
    }
    return baselineSorted;
  }, [
    representativeScopedContentRows,
    representativeScopedRowsFromCache,
    representativeTrackWorkspaceEntry?.isDirty,
    representativeTrackWorkspaceEntry?.persistKind,
    representativeTrackWorkspaceEntry?.rows,
    representativeWorkspaceScopeId,
  ]);
  const representativeAlignedRowsForIndicators = React.useMemo(() => {
    if (!representativeEffectiveScopedRowsForIndicators.length) return [];
    const sorted = [...representativeEffectiveScopedRowsForIndicators].sort(compareContentRowsByOrder);
    const rematched =
      workspaceId && effectiveGradeId && termNumber && representativeScopeKey
        ? assembleTrackScopedRowsForSchedule(sorted, representativeScheduleAnnualKeys, {
            workspaceId,
            gradeId: effectiveGradeId,
            academicYear,
            termNumber,
            scopeKey: representativeScopeKey,
          })
        : sorted;
    return alignProgramContentRowsToScheduleSlotLayout({
      contentRowsSorted: rematched,
      baselineRows: baselineWorkspaceUpserts,
    });
  }, [
    academicYear,
    baselineWorkspaceUpserts,
    effectiveGradeId,
    representativeEffectiveScopedRowsForIndicators,
    representativeScheduleAnnualKeys,
    representativeScopeKey,
    termNumber,
    workspaceId,
  ]);
  const effectiveScopedRows = React.useMemo(
    () => {
      const baselineSorted = [...scopedRowsForSchedulePairing].sort(compareContentRowsByOrder);
      if (isSyncMirrorView && representativeTrackWorkspaceEntry?.isDirty && representativeTrackWorkspaceEntry.rows.length) {
        const merged = mergeProgramWorkspaceUpsertsIntoBaselineRows(
          baselineSorted,
          representativeTrackWorkspaceEntry.rows,
          {
            persistKind:
              representativeTrackWorkspaceEntry.persistKind === 'replace' ? 'replace' : 'patch',
            identity: 'id-position',
          }
        );
        return merged.map((row, idx) => {
          const targetScheduleRow = filteredRows[idx];
          const targetAnnualKey = targetScheduleRow?.annualCellKey ?? null;
          const targetWorkspaceRow = trackWorkspaceEntry?.rows?.[idx];
          const nextBg = { ...(row.bg_color_by_field ?? {}) };
          if (targetWorkspaceRow?.bg_color_by_field?.record_class_text) {
            nextBg.record_class_text = targetWorkspaceRow.bg_color_by_field.record_class_text;
          } else {
            delete nextBg.record_class_text;
          }
          const mirrorRecRaw = targetWorkspaceRow?.record_class_text ?? '';
          const mirrorRec = mirrorRecRaw.trim() !== '' ? mirrorRecRaw : (row.record_class_text ?? '');
          return {
            ...row,
            id: row.id ?? `virtual-${trackWorkspaceScopeId}-${idx}`,
            source_annual_cell_key: targetAnnualKey ?? row.source_annual_cell_key ?? null,
            record_class_text: mirrorRec,
            bg_color_by_field: nextBg,
          };
        });
      }
      if (trackWorkspaceEntry?.isDirty && trackWorkspaceEntry.rows?.length) {
        /** LS/훅으로 맞춘 현재 편제 baseline 위에 워크스페이스 더티만 얹어 표시(통합 저장 전 편제 간 이동 시에도 baseline은 항상 해당 스코프 캐시) */
        const merged = mergeProgramWorkspaceUpsertsIntoBaselineRows(baselineSorted, trackWorkspaceEntry.rows, {
          persistKind: trackWorkspaceEntry.persistKind === 'replace' ? 'replace' : 'patch',
          identity: 'id-position',
        });
        return merged.map((row, idx) => ({
          ...row,
          id: row.id ?? `virtual-${trackWorkspaceScopeId}-${idx}`,
        }));
      }
      if (isSyncMirrorView) {
        const repSource = representativeEffectiveScopedRowsForIndicators.length
          ? representativeEffectiveScopedRowsForIndicators
          : representativeScopedContentRows.length
            ? representativeScopedContentRows
            : representativeScopedRowsFromCache;
        if (repSource.length) {
          const rematchedRep =
            representativeScopeKey && workspaceId && effectiveGradeId && termNumber
              ? assembleTrackScopedRowsForSchedule(repSource, representativeScheduleAnnualKeys, {
                  workspaceId,
                  gradeId: effectiveGradeId,
                  academicYear,
                  termNumber,
                  scopeKey: representativeScopeKey,
                })
              : repSource;
          return overlaySyncMirrorProgramContentFromRepresentative(baselineSorted, rematchedRep);
        }
      }
      return scopedRowsForSchedulePairing;
    },
    [
      academicYear,
      effectiveGradeId,
      filteredRows,
      isSyncMirrorView,
      representativeEffectiveScopedRowsForIndicators,
      representativeScheduleAnnualKeys,
      representativeScopeKey,
      representativeScopedContentRows,
      representativeScopedRowsFromCache,
      representativeTrackWorkspaceEntry,
      scopedRowsForSchedulePairing,
      termNumber,
      trackWorkspaceEntry?.rows,
      trackWorkspaceEntry?.persistKind,
      representativeTrackWorkspaceEntry?.persistKind,
      trackWorkspaceScopeId,
      workspaceId,
      /** `getProgramWorkspaceScopeEntry`는 LS 직독이라, 워크스페이스 이벤트만으로는 메모 deps가 안 바뀌어 1·2학기 전환 후 UI가 고정될 수 있음 */
      workspaceStateRev,
    ]
  );
  const alignedContentRows = React.useMemo(() => {
    const sorted = [...effectiveScopedRows].sort(compareContentRowsByOrder);
    return alignProgramContentRowsToScheduleSlotLayout({
      contentRowsSorted: sorted,
      baselineRows: baselineWorkspaceUpserts,
    });
  }, [baselineWorkspaceUpserts, effectiveScopedRows]);
  /** 연간 일정 슬롯이 없을 때만 최소 빈 1행(미리 편집용). 일정이 있으면 정렬 결과만 사용 */
  const contentRowsForProgramTable = React.useMemo(() => {
    let rows =
      filteredRows.length > 0
        ? alignedContentRows
        : !workspaceId || !effectiveGradeId || !termNumber
          ? alignedContentRows
          : withMinimumProgramContentRowsForDisplay(alignedContentRows, {
              workspaceId,
              gradeId: effectiveGradeId,
              academicYear,
              termNumber,
            });
    /** dirty여도 DB/캐시 잔여 빈 넘침 행은 숨김(temp- 수업추가 행만 trim이 보존) */
    rows = trimTrailingEmptyProgramContentOverflowRows(rows);
    return rows;
  }, [
    academicYear,
    alignedContentRows,
    effectiveGradeId,
    filteredRows.length,
    termNumber,
    workspaceId,
  ]);
  const programImportLearningBlocked = React.useMemo(
    () => programAlignedImportColumnsHaveData(alignedContentRows),
    [alignedContentRows]
  );
  const { tableRows, programCellsByAnnualKey } = React.useMemo(() => {
    const mergedRows: typeof filteredRows = [];
    const cellMap: Record<string, ProgramProgressCell> = {};
    contentRowsForProgramTable.forEach((contentRow, orderIndex) => {
      /** 스코프 행 순서 = 표 행 순서. i번째 행의 일정 열은 항상 i번째 연간 일정(없으면 넘침 행 → 일정 열만 비활성) */
      const schedule = orderIndex < filteredRows.length ? filteredRows[orderIndex] : undefined;
      const annualCellKey = schedule ? schedule.annualCellKey : `orphan:${contentRow.id ?? orderIndex}`;
      const row = schedule
        ? {
            ...schedule,
            fixedContentId: contentRow.id,
            fixedOrderIndex: orderIndex,
            fixedSourceAnnualCellKey: schedule.annualCellKey,
            isScheduleAvailable: true,
            isScheduleSlotOverflow: false,
          }
        : {
            annualCellKey,
            gradeId: effectiveGradeId,
            mondayIso: '',
            weekdayKey: 'mon' as const,
            slotIndex: orderIndex,
            seq: 0,
            dateLabel: '',
            weekdayLabel: '',
            periodLabel: '',
            gradeLabel: '',
            semesterLabel: '',
            templateLabel: '',
            timetableText: '',
            timetableColorHex: '',
            timetableTextShrink: false,
            timetableSlotKind: 'non_class' as const,
            programKindLabel: '',
            memo: '',
            isScheduleAvailable: false,
            isScheduleSlotOverflow: true,
            fixedContentId: contentRow.id,
            fixedOrderIndex: orderIndex,
            fixedSourceAnnualCellKey: undefined,
          };
      mergedRows.push(row);

      cellMap[annualCellKey] = {
        id: contentRow.id ?? '',
        workspace_id: workspaceId,
        grade_id: effectiveGradeId,
        academic_year: academicYear,
        term_number: termNumber ?? 1,
        monday_iso: schedule?.mondayIso ?? '',
        weekday_key: schedule?.weekdayKey ?? 'mon',
        slot_index: schedule?.slotIndex ?? orderIndex,
        timetable_text: schedule?.timetableText ?? '',
        unit_text: contentRow.unit_text ?? '',
        learning_text: contentRow.learning_text ?? '',
        learning_note: contentRow.learning_note ?? '',
        presentation_ref_id: contentRow.presentation_ref_id ?? null,
        worksheet_ref_id: contentRow.worksheet_ref_id ?? null,
        page_text: contentRow.page_text ?? '',
        assistant_page_text: contentRow.assistant_page_text ?? '',
        current_period_text: contentRow.current_period_text ?? '',
        total_period_text: contentRow.total_period_text ?? '',
        material_text: contentRow.material_text ?? '',
        record_cross_curricular_text: contentRow.record_cross_curricular_text ?? '',
        record_memo_text: contentRow.record_memo_text ?? '',
        record_evaluation_text: contentRow.record_evaluation_text ?? '',
        record_class_text: contentRow.record_class_text ?? '',
        record_member_group_id: contentRow.record_member_group_id ?? null,
        bg_color_by_field: contentRow.bg_color_by_field ?? {},
      };
    });
    return { tableRows: mergedRows, programCellsByAnnualKey: cellMap };
  }, [academicYear, contentRowsForProgramTable, effectiveGradeId, filteredRows, workspaceId, termNumber]);
  const liveProgramTableDisplay = React.useMemo(
    () => ({ tableRows, programCellsByAnnualKey }),
    [programCellsByAnnualKey, tableRows]
  );
  const {
    display: heldProgramTableDisplay,
    holding: postSaveHolding,
    beginHold,
    endHold,
  } = useProgramPostSaveDisplayHold(liveProgramTableDisplay);
  const displayTableRows = heldProgramTableDisplay.tableRows;
  const displayProgramCellsByAnnualKey = heldProgramTableDisplay.programCellsByAnnualKey;
  const showTableLoading = tableLoading && !postSaveHolding;

  const syncMirrorRepAnnualCellKeyByMirrorKey = React.useMemo((): Record<string, string> => {
    if (!isSyncActive || !syncRepAlias) return {};
    const repFiltered = rows.filter((row) => normalizeRowTimetableAlias(row) === syncRepAlias);
    const map: Record<string, string> = {};
    const mirrorAliases = isSyncMirrorView
      ? [displayTimetableName].filter(Boolean)
      : programTrackAliasesOrdered.filter((alias) => alias !== syncRepAlias);
    for (const mirrorAlias of mirrorAliases) {
      const mirrorFiltered = rows.filter((row) => normalizeRowTimetableAlias(row) === mirrorAlias);
      mirrorFiltered.forEach((mirrorRow, index) => {
        const repRow = repFiltered[index];
        const mirrorKey = mirrorRow.annualCellKey?.trim();
        const repKey = repRow?.annualCellKey?.trim();
        if (mirrorKey && repKey) map[mirrorKey] = repKey;
      });
    }
    return map;
  }, [
    displayTimetableName,
    isSyncActive,
    isSyncMirrorView,
    normalizeRowTimetableAlias,
    programTrackAliasesOrdered,
    rows,
    syncRepAlias,
  ]);

  const syncMirrorMirrorAnnualCellKeysByRepKey = React.useMemo((): Record<string, string[]> => {
    const reverse: Record<string, string[]> = {};
    for (const [mirrorKey, repKey] of Object.entries(syncMirrorRepAnnualCellKeyByMirrorKey)) {
      const rk = repKey.trim();
      const mk = mirrorKey.trim();
      if (!rk || !mk) continue;
      if (!reverse[rk]) reverse[rk] = [];
      if (!reverse[rk].includes(mk)) reverse[rk].push(mk);
    }
    return reverse;
  }, [syncMirrorRepAnnualCellKeyByMirrorKey]);

  const syncMirrorIndicatorCellsByAnnualKey = React.useMemo((): Record<string, ProgramProgressCell> => {
    if (!isSyncMirrorView || !representativeAlignedRowsForIndicators.length) return {};
    const map: Record<string, ProgramProgressCell> = {};
    representativeAlignedRowsForIndicators.forEach((contentRow, orderIndex) => {
      const schedule = orderIndex < filteredRows.length ? filteredRows[orderIndex] : undefined;
      if (!schedule) return;
      /** 동기화 미러 인디케이터: 대표 scoped 행 learning_text만 사용(미러 program_cells의 stale ref 병합 방지) */
      const learningText = contentRow.learning_text ?? '';
      map[schedule.annualCellKey] = {
        id: contentRow.id ?? '',
        workspace_id: workspaceId,
        grade_id: effectiveGradeId,
        academic_year: academicYear,
        term_number: termNumber ?? 1,
        monday_iso: schedule.mondayIso ?? '',
        weekday_key: schedule.weekdayKey ?? 'mon',
        slot_index: schedule.slotIndex ?? orderIndex,
        timetable_text: schedule.timetableText ?? '',
        unit_text: contentRow.unit_text ?? '',
        learning_text: learningText,
        learning_note: contentRow.learning_note ?? '',
        presentation_ref_id: contentRow.presentation_ref_id ?? null,
        worksheet_ref_id: contentRow.worksheet_ref_id ?? null,
        page_text: contentRow.page_text ?? '',
        assistant_page_text: contentRow.assistant_page_text ?? '',
        current_period_text: contentRow.current_period_text ?? '',
        total_period_text: contentRow.total_period_text ?? '',
        material_text: contentRow.material_text ?? '',
        record_cross_curricular_text: contentRow.record_cross_curricular_text ?? '',
        record_memo_text: contentRow.record_memo_text ?? '',
        record_evaluation_text: contentRow.record_evaluation_text ?? '',
        record_class_text: contentRow.record_class_text ?? '',
        record_member_group_id: contentRow.record_member_group_id ?? null,
        bg_color_by_field: contentRow.bg_color_by_field ?? {},
      };
    });
    return map;
  }, [
    academicYear,
    effectiveGradeId,
    filteredRows,
    isSyncMirrorView,
    representativeAlignedRowsForIndicators,
    termNumber,
    workspaceId,
  ]);

  const programDocumentExportTimetableName = React.useMemo(
    () => resolveProgramExportTimetableName(displayTimetableName, tableRows, programCellsByAnnualKey),
    [displayTimetableName, programCellsByAnnualKey, tableRows]
  );

  const programDocumentExportTitleParts = React.useMemo(
    () =>
      buildProgramExportTrackTimetableParts({
        trackName: decodedTrack,
        timetableName: programDocumentExportTimetableName,
      }),
    [decodedTrack, programDocumentExportTimetableName]
  );

  const programDocumentExportTitle = React.useMemo(
    () =>
      buildProgramTrackDocumentTitle({
        trackName: decodedTrack,
        timetableName: programDocumentExportTimetableName,
      }),
    [decodedTrack, programDocumentExportTimetableName]
  );

  const programDocumentScopeLabel = React.useMemo(
    () => buildProgramDocumentScopeLabel(termNumber ?? 1),
    [termNumber]
  );

  const programDocumentExportFileBase = React.useMemo(
    () =>
      buildProgramLearningContentExportFileBase({
        scopeLabel: classParsed?.title ?? '클래스',
        programLabel: [decodedTrack, displayTimetableName].filter(Boolean).join('_') || '편제',
        termNumber: termNumber ?? 1,
      }),
    [classParsed?.title, decodedTrack, displayTimetableName, termNumber]
  );

  const {
    canExport: canExportProgramDocument,
    exportBusy: programDocumentExportBusy,
    handleSaveExcel: handleProgramDocumentSaveExcel,
    handleSavePdf: handleProgramDocumentSavePdf,
    handlePrint: handleProgramDocumentPrint,
  } = useProgramLearningContentDocumentExport({
    tableRows,
    programCellsByAnnualKey,
    scopeLabel: programDocumentScopeLabel,
    programTitle: programDocumentExportTitle,
    programTitleParts: programDocumentExportTitleParts,
    scopeBadge: termNumber === 2 ? 'term2' : 'term1',
    fileNameBase: programDocumentExportFileBase,
  });

  const scheduleRowsByAlias = React.useMemo(() => {
    const out = new Map<string, typeof rows>();
    rows.forEach((row) => {
      const alias = normalizeRowTimetableAlias(row);
      if (!alias) return;
      const prev = out.get(alias) ?? [];
      prev.push(row);
      out.set(alias, prev);
    });
    return out;
  }, [normalizeRowTimetableAlias, rows]);

  /** 통합 저장 시 `saveTrackScope`가 동기화 alias별 `program_scoped_content_rows`·진도셀에도 fan-out 하도록 메타 제공 */
  const trackSyncWorkspaceFanout = React.useMemo((): Pick<
    ProgramWorkspaceScopeEntry,
    'trackSyncFanoutScopeKeys' | 'trackSyncScheduleAnnualKeysByScope'
  > | null => {
    if (!isSyncActive || !decodedTrack || programTrackAliasesOrdered.length < 2) return null;
    const keys = programTrackAliasesOrdered.map((alias) => `${decodedTrack}|${alias}`) as readonly string[];
    const scheduleAnnualKeysByScope: Record<string, readonly string[]> = {};
    keys.forEach((sk) => {
      const alias = sk.split('|').slice(1).join('|').trim();
      const scheduleRows = scheduleRowsByAlias.get(alias) ?? [];
      scheduleAnnualKeysByScope[sk] = scheduleRows.map((r) => r.annualCellKey);
    });
    return {
      trackSyncFanoutScopeKeys: keys,
      trackSyncScheduleAnnualKeysByScope: scheduleAnnualKeysByScope,
    };
  }, [decodedTrack, isSyncActive, programTrackAliasesOrdered, scheduleRowsByAlias]);

  const upsertScopeContentRows = React.useCallback(
    async (rowsToSave: ProgramFixedContentRowUpsert[]) => {
      if (!effectiveGradeId || !termNumber) return;
      const normalized = rowsToSave.map((row, orderIndex) => {
        const learningWritten = writeProgramLearningContentFields(readProgramLearningContentFields(row));
        return {
          id:
            row.id?.startsWith('virtual-') || row.id?.startsWith('extra:') || row.id?.startsWith('temp-')
              ? undefined
              : row.id,
          workspace_id: workspaceId,
          grade_id: effectiveGradeId,
          academic_year: academicYear,
          term_number: termNumber,
          scope_type: 'track' as const,
          scope_key: scopeKey,
          /** 행 순서 = 표에서의 위치(0부터). 연간칸 키로 짝짓지 않는다 */
          order_index: orderIndex,
          source_annual_cell_key: null,
          unit_text: row.unit_text ?? '',
          learning_text: learningWritten.learning_text ?? '',
          learning_note: learningWritten.learning_note ?? '',
          presentation_ref_id: learningWritten.presentation_ref_id,
          worksheet_ref_id: learningWritten.worksheet_ref_id,
          page_text: row.page_text ?? '',
          assistant_page_text: row.assistant_page_text ?? '',
          current_period_text: row.current_period_text ?? '',
          total_period_text: row.total_period_text ?? '',
          material_text: row.material_text ?? '',
          record_cross_curricular_text: row.record_cross_curricular_text ?? '',
          record_memo_text: row.record_memo_text ?? '',
          record_evaluation_text: row.record_evaluation_text ?? '',
          record_class_text: row.record_class_text ?? '',
          record_member_group_id: row.record_member_group_id ?? null,
          bg_color_by_field: row.bg_color_by_field ?? {},
        };
      });
      const fanoutScopeKeys =
        isSyncActive && decodedTrack
          ? programTrackAliasesOrdered.map((alias) => `${decodedTrack}|${alias}`)
          : [scopeKey];
      const uniqueScopeKeys = Array.from(new Set([scopeKey, ...fanoutScopeKeys].filter(Boolean)));
      const primaryBaseline = [...rowsForDisplayScope].sort(compareContentRowsByOrder);
      /** 대표 행 id → 기준본에서의 위치. 동기화 반(alias) 행은 같은 위치의 행을 짝으로 삼아 내용과 함께 이동 */
      const primaryBaselinePosById = new Map<string, number>();
      primaryBaseline.forEach((row, idx) => primaryBaselinePosById.set(row.id.toLowerCase(), idx));
      const existingByScope = new Map<string, ProgramScopedContentRow[]>();
      const otherScopeKeys = uniqueScopeKeys.filter((targetScopeKey) => targetScopeKey !== scopeKey);
      const fetchedScopes = await Promise.all(
        otherScopeKeys.map(async (targetScopeKey) => ({
          targetScopeKey,
          existing: await getProgramScopedContentRows(
            workspaceId,
            academicYear,
            termNumber,
            effectiveGradeId,
            'track',
            targetScopeKey
          ),
        }))
      );
      fetchedScopes.forEach(({ targetScopeKey, existing }) => {
        existingByScope.set(targetScopeKey, existing);
      });
      const buildSnapshotForScope = (
        targetScopeKey: string,
        savedPrimaryByOrder?: Map<number, ProgramScopedContentRow>
      ) => {
        const isPrimary = targetScopeKey === scopeKey;
        const existingRows = isPrimary
          ? primaryBaseline
          : [...(existingByScope.get(targetScopeKey) ?? [])].sort(compareContentRowsByOrder);
        const snapshot = normalized.map((row) => {
          const primaryPos = row.id ? primaryBaselinePosById.get(row.id.toLowerCase()) : undefined;
          const peer = isPrimary || primaryPos == null ? undefined : existingRows[primaryPos];
          /** 대표 저장에서 증배 첨부가 사본으로 분리됐으면 반(alias)도 같은 ref를 가리킨다 */
          const savedPrimary = savedPrimaryByOrder?.get(row.order_index);
          const fanoutBase = {
            ...row,
            ...(savedPrimary
              ? {
                  learning_text: savedPrimary.learning_text ?? '',
                  learning_note: savedPrimary.learning_note ?? '',
                  presentation_ref_id: savedPrimary.presentation_ref_id ?? null,
                  worksheet_ref_id: savedPrimary.worksheet_ref_id ?? null,
                }
              : {}),
            id: isPrimary ? row.id : peer?.id,
            scope_key: targetScopeKey,
            /** 위치(order_index)로만 짝짓는다 — 연간칸 키는 저장하지 않음 */
            source_annual_cell_key: null,
          };
          return isPrimary ? fanoutBase : preserveIndependentScopedRecordFieldsOnFanout(fanoutBase, peer);
        });
        return { existingRows, snapshot };
      };
      const primary = buildSnapshotForScope(scopeKey);
      const savedPrimary = await saveScopedSnapshotRows(primary.snapshot, primary.existingRows);
      if (!otherScopeKeys.length) return;
      const savedPrimaryByOrder = new Map<number, ProgramScopedContentRow>();
      savedPrimary.forEach((row) => {
        if (!savedPrimaryByOrder.has(row.order_index)) savedPrimaryByOrder.set(row.order_index, row);
      });
      await Promise.all(
        otherScopeKeys.map(async (targetScopeKey) => {
          const { existingRows, snapshot } = buildSnapshotForScope(targetScopeKey, savedPrimaryByOrder);
          await saveProgramScopedContentRowsDiff(
            {
              workspaceId,
              academicYear,
              termNumber,
              gradeId: effectiveGradeId,
              scopeType: 'track',
              scopeKey: targetScopeKey,
              baselineRows: existingRows,
              snapshotRows: snapshot,
              skipRefFork: true,
            },
            { deferSchoolVersionNotify: true }
          );
        })
      );
      await notifySchoolProgramMutation(workspaceId);
    },
    [
      academicYear,
      decodedTrack,
      effectiveGradeId,
      isSyncActive,
      programTrackAliasesOrdered,
      rowsForDisplayScope,
      saveScopedSnapshotRows,
      workspaceId,
      scopeKey,
      termNumber,
    ]
  );
  const handleApplyProgramLearningImport = React.useCallback(
    async (adminRows: AdminLearningContentRow[]) => {
      if (!workspaceId || !effectiveGradeId || !termNumber || !scopeKey) return;
      if (programAlignedImportColumnsHaveData(alignedContentRows)) return;
      setImportLearningApplying(true);
      try {
        // 연간시간표 일정이 없어도 빈 표에 학습내용 행을 구성할 수 있게 seedMeta 전달
        const merged = mergeProgramBaselineWithAdminLearningContent(alignedContentRows, adminRows, {
          workspaceId,
          gradeId: effectiveGradeId,
          academicYear,
          termNumber,
        });
        const baseline = persistedAlignedWorkspaceUpserts;
        const dirty =
          baseline.length !== merged.length ||
          programFixedWorkspaceRowsDigest(merged) !== programFixedWorkspaceRowsDigest(baseline);
        if (!dirty) {
          setImportLearningModalOpen(false);
          return;
        }
        upsertProgramWorkspaceScopeEntry({
          scopeId: trackWorkspaceScopeId,
          scopeType: 'track',
          scopeKey,
          workspaceId,
          gradeId: effectiveGradeId,
          academicYear,
          termNumber,
          rows: merged,
          isDirty: true,
          updatedAt: Date.now(),
          persistKind: resolveProgramWorkspacePersistKind(undefined, merged.length, baseline.length),
          ...(trackSyncWorkspaceFanout ?? {}),
        });
        setImportLearningModalOpen(false);
      } finally {
        setImportLearningApplying(false);
      }
    },
    [
      academicYear,
      alignedContentRows,
      effectiveGradeId,
      persistedAlignedWorkspaceUpserts,
      scopeKey,
      termNumber,
      trackSyncWorkspaceFanout,
      trackWorkspaceScopeId,
      workspaceId,
    ]
  );
  const exportLearningTemplateName = React.useMemo(
    () => (decodedTrack || '편제').trim(),
    [decodedTrack]
  );
  const handleExportProgramLearning = React.useCallback(async () => {
    if (!workspaceId || !termNumber || !scopeKey) return;
    setExportLearningApplying(true);
    try {
      const subCategory = termNumber === 1 ? 'term1' : 'term2';
      const result = await exportProgramLearningToWorkspace({
        workspaceId,
        gradeId: effectiveGradeId,
        gradeNumber: defaultGradeNumberForImport,
        subCategory,
        templateName: exportLearningTemplateName,
        programRows: alignedContentRows,
      });
      setExportLearningModalOpen(false);
      setSaveToastState({
        kind: 'success',
        message: result.replacedPriorExport
          ? `기본 학습내용 등록 "${exportLearningTemplateName}" 편제의 기존 내보내기 데이터를 대체했습니다.`
          : `기본 학습내용 등록 "${exportLearningTemplateName}" 편제로 내보냈습니다.`,
      });
    } catch (e) {
      setSaveToastState({
        kind: 'error',
        message: e instanceof Error ? e.message : '학습내용 내보내기에 실패했습니다.',
      });
    } finally {
      setExportLearningApplying(false);
    }
  }, [
    alignedContentRows,
    defaultGradeNumberForImport,
    effectiveGradeId,
    exportLearningTemplateName,
    scopeKey,
    termNumber,
    workspaceId,
  ]);
  const handleImportCsvFileSelected = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.currentTarget.value = '';
      if (!file || !termNumber) return;
      if (programAlignedImportColumnsHaveData(alignedContentRows)) {
        setSaveToastState({
          kind: 'error',
          message: PROGRAM_IMPORT_LEARNING_CONTENT_BLOCKED_MESSAGE,
        });
        return;
      }
      setImportLearningApplying(true);
      try {
        const text = await file.text();
        const subCategory = termNumber === 1 ? 'term1' : 'term2';
        const parsedRows = parseProgramLearningContentCsv(text, defaultGradeNumberForImport, subCategory);
        if (parsedRows.length === 0) {
          setSaveToastState({
            kind: 'error',
            message: 'CSV 파일에 가져올 학습내용이 없습니다.',
          });
          return;
        }
        await handleApplyProgramLearningImport(parsedRows);
        setSaveToastState({
          kind: 'success',
          message: `CSV에서 ${parsedRows.length}개 행을 가져왔습니다.`,
        });
      } catch (error) {
        setSaveToastState({
          kind: 'error',
          message: error instanceof Error ? error.message : 'CSV 파일 가져오기에 실패했습니다.',
        });
      } finally {
        setImportLearningApplying(false);
      }
    },
    [
      alignedContentRows,
      defaultGradeNumberForImport,
      handleApplyProgramLearningImport,
      termNumber,
    ]
  );
  const handleExportCsvFile = React.useCallback(() => {
    try {
      const csv = createProgramLearningContentCsv(alignedContentRows);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const gradeLabel = classParsed?.title ?? 'class';
      const scopeLabel = (decodedTrack || '편제').trim() || '편제';
      const termLabel = termNumber ? `${termNumber}학기` : '학기';
      const fileName = `${gradeLabel}_${scopeLabel}_${termLabel}_학습내용.csv`.replace(/[\\/:*?"<>|]/g, '_');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSaveToastState({
        kind: 'success',
        message: 'CSV 파일로 내보냈습니다.',
      });
    } catch (error) {
      setSaveToastState({
        kind: 'error',
        message: error instanceof Error ? error.message : 'CSV 파일 내보내기에 실패했습니다.',
      });
    }
  }, [alignedContentRows, classParsed?.title, decodedTrack, termNumber]);
  const [saveAction, setSaveAction] = React.useState<(() => Promise<void>) | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [saveToastState, setSaveToastState] = React.useState<
    | {
        kind: 'saving' | 'success' | 'error';
        message: string;
      }
    | null
  >(null);
  const [slideDraftDirtyRev, setSlideDraftDirtyRev] = React.useState(0);
  const [worksheetDraftDirtyRev, setWorksheetDraftDirtyRev] = React.useState(0);
  const handleBindSaveAction = React.useCallback((fn: (() => Promise<void>) | null) => {
    setSaveAction(() => fn ?? null);
  }, []);
  const { setProgramProgressDirty, saveProgramProgressRef } = React.useContext(GradeTrackLeaveContext);
  React.useEffect(() => {
    const refresh = () => setWorkspaceStateRev((v) => v + 1);
    window.addEventListener(PROGRAM_WORKSPACE_CHANGED_EVENT, refresh as EventListener);
    return () => window.removeEventListener(PROGRAM_WORKSPACE_CHANGED_EVENT, refresh as EventListener);
  }, []);
  React.useEffect(() => {
    if (!activeWorkspaceScopeId) return;
    pruneProgramWorkspaceScopes([activeWorkspaceScopeId]);
  }, [activeWorkspaceScopeId]);
  /** LS에 replace+가짜 1행만 남은 경우, 이후 DB 행이 늘어나도 병합이 1행으로 줄어드는 것 방지 */
  React.useEffect(() => {
    if (!trackWorkspaceScopeId || scopedContentRows.length === 0) return;
    const ent = getProgramWorkspaceScopeEntry(trackWorkspaceScopeId);
    if (!ent?.isDirty || ent.persistKind !== 'replace' || !ent.rows?.length) return;
    if (ent.rows.length >= scopedContentRows.length) return;
    if (!isPristineMinimumProgramContentPlaceholderRows(ent.rows)) return;
    removeProgramWorkspaceScopeEntry(trackWorkspaceScopeId);
  }, [trackWorkspaceScopeId, scopedContentRows.length]);
  React.useEffect(() => {
    const disposers: Array<() => void> = [];
    if (trackWorkspaceScopeId) disposers.push(registerDiscardDirtyWorkspaceOnPageHide(trackWorkspaceScopeId));
    if (
      representativeWorkspaceScopeId &&
      representativeWorkspaceScopeId !== trackWorkspaceScopeId
    ) {
      disposers.push(registerDiscardDirtyWorkspaceOnPageHide(representativeWorkspaceScopeId));
    }
    return () => disposers.forEach((d) => d());
  }, [representativeWorkspaceScopeId, trackWorkspaceScopeId]);
  const handleWorkspaceRowsChange = React.useCallback(
    (rowsFromTable: ProgramFixedContentRowUpsert[] | null, meta?: ProgramWorkspaceRowsChangeMeta) => {
      if (!effectiveGradeId || !termNumber) return;
      if (rowsFromTable == null) {
        removeProgramWorkspaceScopeEntry(trackWorkspaceScopeId);
        return;
      }
      if (isPristineMinimumProgramContentPlaceholderRows(rowsFromTable)) {
        removeProgramWorkspaceScopeEntry(trackWorkspaceScopeId);
        return;
      }
      const baseline = persistedAlignedWorkspaceUpserts;
      const dirty =
        baseline.length !== rowsFromTable.length ||
        programFixedWorkspaceRowsDigest(rowsFromTable) !== programFixedWorkspaceRowsDigest(baseline);
      upsertProgramWorkspaceScopeEntry({
        scopeId: trackWorkspaceScopeId,
        scopeType: 'track',
        scopeKey,
        workspaceId,
        gradeId: effectiveGradeId,
        academicYear,
        termNumber,
        rows: rowsFromTable,
        isDirty: dirty,
        updatedAt: Date.now(),
        persistKind: resolveProgramWorkspacePersistKind(meta, rowsFromTable.length, baseline.length),
        ...(trackSyncWorkspaceFanout ?? {}),
      });
    },
    [
      academicYear,
      persistedAlignedWorkspaceUpserts,
      effectiveGradeId,
      workspaceId,
      scopeKey,
      termNumber,
      trackSyncWorkspaceFanout,
      trackWorkspaceScopeId,
    ]
  );
  const handleEnsureWorkspaceSnapshot = React.useCallback(
    (rowsFromTable: ProgramFixedContentRowUpsert[]) => {
      if (!effectiveGradeId || !termNumber || !rowsFromTable.length) return;
      /** 일정 0행일 때 UI용 가짜 1행 — LS 스냅샷으로 굳히지 않음(이후 DB 행과 불일치 방지) */
      if (isPristineMinimumProgramContentPlaceholderRows(rowsFromTable)) return;
      ensureProgramWorkspaceScopeSnapshot({
        scopeId: trackWorkspaceScopeId,
        scopeType: 'track',
        scopeKey,
        workspaceId,
        gradeId: effectiveGradeId,
        academicYear,
        termNumber,
        rows: rowsFromTable,
        isDirty: false,
        updatedAt: Date.now(),
      });
    },
    [academicYear, effectiveGradeId, workspaceId, scopeKey, termNumber, trackWorkspaceScopeId]
  );
  React.useEffect(() => {
    if (!saveToastState || saveToastState.kind === 'saving') return;
    const t = window.setTimeout(() => setSaveToastState(null), 1800);
    return () => window.clearTimeout(t);
  }, [saveToastState]);

  const workspaceDirtyScopeIds = React.useMemo(() => {
    const ids: string[] = [];
    if (trackWorkspaceScopeId) ids.push(trackWorkspaceScopeId);
    if (
      representativeWorkspaceScopeId &&
      representativeWorkspaceScopeId !== trackWorkspaceScopeId &&
      !ids.includes(representativeWorkspaceScopeId)
    ) {
      ids.push(representativeWorkspaceScopeId);
    }
    return ids;
  }, [representativeWorkspaceScopeId, trackWorkspaceScopeId]);
  const pageWorkspaceDirty = isAnyProgramWorkspaceScopeDirty(workspaceDirtyScopeIds);
  const trackScopeServerSync = useProgramWorkspaceScopeServerSync({
    scopeId: trackWorkspaceScopeId,
    enabled: dataActive && !!trackWorkspaceScopeId && !!effectiveGradeId && !!termNumber && !!scopeKey,
    serverRows: scopedContentRows,
    fetchHead: React.useCallback(
      () =>
        fetchProgramScopedContentRowsHead(
          workspaceId,
          academicYear,
          termNumber as 1 | 2,
          effectiveGradeId,
          'track',
          scopeKey
        ),
      [academicYear, effectiveGradeId, scopeKey, termNumber, workspaceId]
    ),
    onAutoRefresh: React.useCallback(() => setSaveRefreshRev((v) => v + 1), []),
  });
  const programGradeTermSaveTarget = React.useMemo(
    () => ({
      workspaceId,
      gradeId: effectiveGradeId,
      academicYear,
      termNumber: (termNumber ?? 1) as 1 | 2,
    }),
    [academicYear, effectiveGradeId, termNumber, workspaceId]
  );
  const leavePending = pageWorkspaceDirty || (hasUnsavedChanges && saveAction != null);
  const programSavePending = React.useMemo(() => {
    void workspaceStateRev;
    void slideDraftDirtyRev;
    void worksheetDraftDirtyRev;
    if (!effectiveGradeId || !termNumber) return false;
    return computeProgramSavePending({
      target: programGradeTermSaveTarget,
      hasUnsavedTableChanges: hasUnsavedChanges,
      hasTableSaveAction: !!saveAction,
    });
  }, [
    effectiveGradeId,
    hasUnsavedChanges,
    programGradeTermSaveTarget,
    saveAction,
    slideDraftDirtyRev,
    termNumber,
    worksheetDraftDirtyRev,
    workspaceStateRev,
  ]);
  const programLeaveDirty = pageWorkspaceDirty || leavePending;

  React.useEffect(() => {
    const syncSlides = () => setSlideDraftDirtyRev((v) => v + 1);
    const syncWorksheets = () => setWorksheetDraftDirtyRev((v) => v + 1);
    const onStorage = (e: StorageEvent) => {
      if (!e.key) return;
      if (e.key.startsWith(PROGRAM_CONTENT_SLIDE_DRAFT_PREFIX)) syncSlides();
      if (e.key.startsWith(LEARNING_WORKSHEET_PENDING_PREFIX)) syncWorksheets();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(PROGRAM_CONTENT_SLIDE_DRAFTS_CHANGED_EVENT, syncSlides);
    window.addEventListener(LEARNING_WORKSHEET_PENDING_CHANGED_EVENT, syncWorksheets);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(PROGRAM_CONTENT_SLIDE_DRAFTS_CHANGED_EVENT, syncSlides);
      window.removeEventListener(LEARNING_WORKSHEET_PENDING_CHANGED_EVENT, syncWorksheets);
    };
  }, []);

  const handleSave = React.useCallback(async () => {
    setSaveToastState({ kind: 'saving', message: '저장 중입니다...' });
    beginHold(cloneProgramTableHoldPayload({ tableRows: displayTableRows, programCellsByAnnualKey: displayProgramCellsByAnnualKey }));
    try {
      const workspaceDirty = isAnyProgramWorkspaceScopeDirty(workspaceDirtyScopeIds);
      const shouldRunTableSave = !!(saveAction && (hasUnsavedChanges || workspaceDirty));
      if (shouldRunTableSave) await saveAction!();
      let uiRefreshMode: 'full' | 'cache' = 'full';
      if (effectiveGradeId && termNumber) {
        const result = await saveProgramGradeTermWorkspaceAndAttachments({
          workspaceId,
          gradeId: effectiveGradeId,
          academicYear,
          termNumber,
        });
        uiRefreshMode = result.uiRefreshMode;
      }
      /** 자식 onDirtyChange effect보다 먼저 부모가 남겨 둔 pending 플래그를 비우고, LS·flush 직후 dirty 재계산 */
      setHasUnsavedChanges(false);
      setSlideDraftDirtyRev((v) => v + 1);
      setWorksheetDraftDirtyRev((v) => v + 1);
      setWorkspaceStateRev((v) => v + 1);
      if (uiRefreshMode === 'full') {
        setSaveRefreshRev((v) => v + 1);
      }
      /** cache 저장: LS 동기화·state 커밋 후 hold 해제(빈 표로 되돌아가는 깜박임 방지) */
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });
      endHold();
      setSaveToastState({ kind: 'success', message: '저장되었습니다.' });
    } catch (e) {
      endHold();
      setSaveToastState({
        kind: 'error',
        message: e instanceof Error ? e.message : '저장에 실패했습니다.',
      });
    }
  }, [
    academicYear,
    beginHold,
    displayProgramCellsByAnnualKey,
    displayTableRows,
    effectiveGradeId,
    endHold,
    hasUnsavedChanges,
    saveAction,
    workspaceDirtyScopeIds,
    workspaceId,
    termNumber,
  ]);

  const handleDiscardUnsaved = React.useCallback(() => {
    discardAllProgramFamilyLeaveDrafts();
    endHold();
    setProgramTableMountKey((k) => k + 1);
    setSaveRefreshRev((v) => v + 1);
    setWorkspaceStateRev((v) => v + 1);
    setSlideDraftDirtyRev((v) => v + 1);
    setWorksheetDraftDirtyRev((v) => v + 1);
    setHasUnsavedChanges(false);
  }, [endHold]);

  React.useEffect(() => {
    if (!isActive) return;
    setProgramProgressDirty(programLeaveDirty);
  }, [isActive, programLeaveDirty, setProgramProgressDirty]);
  React.useEffect(() => {
    if (!isActive) return undefined;
    saveProgramProgressRef.current = { save: handleSave, discard: handleDiscardUnsaved };
    return () => {
      saveProgramProgressRef.current = null;
    };
  }, [handleDiscardUnsaved, handleSave, isActive, saveProgramProgressRef]);

  const fixedModeProgramCellFields = React.useMemo((): string[] => [], []);
  const lockedFieldIds = React.useMemo(
    () => (isSyncMirrorView ? MIRROR_LOCKED_FIELD_IDS : []),
    [isSyncMirrorView]
  );

  const emptyProgramTableCard = (
    <ProgramProgressEmptyStateTableCard
      mode="track"
      toolbarVariant="track"
      academicYear={academicYear}
      importProps={{
        workspaceId,
        gradeId: effectiveGradeId,
        defaultGradeNumber: defaultGradeNumberForImport,
        termNumber: termNumber ?? 1,
        preferredTemplateName: decodedTrack,
        modalOpen: importLearningModalOpen,
        onCloseModal: () => {
          if (!importLearningApplying) setImportLearningModalOpen(false);
        },
        onOpenImport: () => setImportLearningModalOpen(true),
        applying: importLearningApplying,
        onApply: handleApplyProgramLearningImport,
        importBlocked: programImportLearningBlocked,
        syncMirrorView: isSyncMirrorView,
      }}
    />
  );

  return (
    <section>
      <div className="cp-program-page-header">
        <h2>
          {classParsed ? classParsed.title : '클래스 미선택'} — {decodedTrack || '편제'} (편제별 프로그램)
          {displayTimetableName ? (
            <>
              {' '}
              <span className="cp-program-page-term-pill">{displayTimetableName}</span>
            </>
          ) : null}
          {termNumber ? (
            <>
              {' '}
              <span className="cp-program-page-term-pill">{termNumber}학기</span>
            </>
          ) : null}
          {isSyncMirrorView ? (
            <>
              {' '}
              <span className="cp-program-sync-pill" title="동기화 미러: 대표 시간표 메뉴에서만 편집할 수 있습니다.">
                {syncRepAlias || decodedTrack || '프로그램'} 동기화 중
              </span>
            </>
          ) : null}
        </h2>
        <div className="cp-program-page-save-actions">
          <ProgramToolbarUndoRedoButtons
            canUndo={programUndoRedoAvail.canUndo}
            canRedo={programUndoRedoAvail.canRedo}
            disabled={isSyncMirrorView}
            onUndo={() => {
              programUndoRedoApiRef.current?.undo();
            }}
            onRedo={() => {
              programUndoRedoApiRef.current?.redo();
            }}
          />
          <button
            type="button"
            className="cp-btn-save-all"
            onClick={handleSave}
            disabled={!programSavePending}
          >
            {programSavePending ? '프로그램 통합 저장하기' : '프로그램 변경사항 없음'}
          </button>
        </div>
      </div>
      {!effectiveGradeId ? (
        <>
          <p className="page-desc">연간진도표에서 클래스를 선택하면 이 화면이 채워집니다.</p>
          {emptyProgramTableCard}
        </>
      ) : !termNumber ? (
        <>
          <p className="page-desc">왼쪽 편제별 프로그램에서 1학기 또는 2학기 메뉴를 선택해 주세요.</p>
          {emptyProgramTableCard}
        </>
      ) : error ? (
        <p className="page-desc" role="alert">{error}</p>
      ) : showTableLoading ? (
        <p className="page-desc">불러오는 중…</p>
      ) : !selectedEntry ? (
        <>{emptyProgramTableCard}</>
      ) : !termDef ? (
        <>
          <p className="page-desc">해당 학기의 학사일정이 없습니다.</p>
          {emptyProgramTableCard}
        </>
      ) : isSyncMirrorView && filteredRows.length === 0 ? (
        <>
          <p className="page-desc">
            동기화 중인 시간표명에서는 편집할 수 없습니다. 대표 시간표명(&quot;{syncRepAlias}&quot;) 메뉴에서 연간
            일정을 만든 뒤 입력해 주세요. (고정/프로젝트 시간은 편제 일정에서 제외됩니다.)
          </p>
          {emptyProgramTableCard}
        </>
      ) : (
        <>
          {filteredRows.length === 0 ? (
            <p className="page-desc">
              해당 학기 연간 시간표에 &quot;{decodedTrack}&quot; 편제 일정이 아직 없습니다. 프로그램 내용(단원·학습
              등)은 미리 입력할 수 있으며, 연간에 시간표가 생기면 일정 열이 채워지고 같은 순서의 행과 맞춰집니다.
              (고정/프로젝트 시간은 제외됩니다.)
            </p>
          ) : null}
          <ProgramWorkspaceStaleConflictBanner
            visible={trackScopeServerSync.staleConflict}
            onAcceptServer={trackScopeServerSync.acceptServerRefresh}
            onDismiss={trackScopeServerSync.dismissStaleConflict}
          />
          <article className="cp-card cp-weekly-class-hours-card cp-program-progress-page-table-card">
            <div className="cp-weekly-class-hours-controls cp-program-progress-table-card-toolbar">
              <div className="cp-program-progress-table-card-toolbar-left">
                <label>
                  학년도
                  <span
                    className="cp-academic-year-readonly"
                    aria-label={`학년도 ${academicYear}학년도(학사일정 기준 고정)`}
                  >
                    {academicYear}학년도
                  </span>
                </label>
              </div>
              <div className="cp-program-progress-toolbar-trailing-actions">
              <div className="cp-program-progress-toolbar-group" role="group" aria-label="학습내용 가져오기/내보내기">
                <ProgramImportLearningDisabledHint
                  hint={resolveProgramImportLearningDisabledHint({
                    contentBlocked: programImportLearningBlocked,
                    syncMirror: isSyncMirrorView,
                  })}
                >
                <button
                  type="button"
                  className="cp-btn-secondary cp-program-import-learning-toolbar-btn"
                  disabled={programImportLearningBlocked || importLearningApplying || exportLearningApplying || isSyncMirrorView}
                  title={
                    isSyncMirrorView || programImportLearningBlocked
                      ? undefined
                      : '가져오기 방식을 선택합니다.'
                  }
                  onClick={() => setImportMethodSelectOpen(true)}
                >
                  학습내용 가져오기
                </button>
                </ProgramImportLearningDisabledHint>
                <button
                  type="button"
                  className="cp-btn-secondary cp-program-import-learning-toolbar-btn"
                  disabled={importLearningApplying || exportLearningApplying || isSyncMirrorView}
                  title={
                    isSyncMirrorView
                      ? '동기화 미러 화면에서는 학습내용 내보내기를 사용할 수 없습니다.'
                      : '내보내기 방식을 선택합니다.'
                  }
                  onClick={() => setExportMethodSelectOpen(true)}
                >
                  학습내용 내보내기
                </button>
              </div>
                <ProgramTableDocumentExportButtons
                  onSaveExcel={handleProgramDocumentSaveExcel}
                  onSavePdf={handleProgramDocumentSavePdf}
                  onPrint={handleProgramDocumentPrint}
                  disabled={!canExportProgramDocument}
                  busy={programDocumentExportBusy || importLearningApplying || exportLearningApplying}
                />
              </div>
            </div>
            <ProgramLearningContentActionSelectModal
              open={importMethodSelectOpen}
              mode="import"
              busy={importLearningApplying || exportLearningApplying}
              onClose={() => setImportMethodSelectOpen(false)}
              onSelectCsv={() => {
                setImportMethodSelectOpen(false);
                importCsvInputRef.current?.click();
              }}
              onSelectWorkspace={() => {
                setImportMethodSelectOpen(false);
                setImportLearningModalOpen(true);
              }}
            />
            <ProgramLearningContentActionSelectModal
              open={exportMethodSelectOpen}
              mode="export"
              busy={importLearningApplying || exportLearningApplying}
              onClose={() => setExportMethodSelectOpen(false)}
              onSelectCsv={() => {
                setExportMethodSelectOpen(false);
                handleExportCsvFile();
              }}
              onSelectWorkspace={() => {
                setExportMethodSelectOpen(false);
                setExportLearningModalOpen(true);
              }}
            />
            <input
              ref={importCsvInputRef}
              type="file"
              accept=".csv,text/csv,.tsv,text/tab-separated-values"
              style={{ display: 'none' }}
              onChange={(event) => {
                void handleImportCsvFileSelected(event);
              }}
            />
            <ProgramExportLearningContentModal
              open={exportLearningModalOpen}
              onClose={() => {
                if (!exportLearningApplying) setExportLearningModalOpen(false);
              }}
              applying={exportLearningApplying}
              gradeNumber={defaultGradeNumberForImport}
              termNumber={termNumber ?? 1}
              templateName={exportLearningTemplateName}
              onConfirm={handleExportProgramLearning}
            />
            <ProgramImportLearningContentModal
              open={importLearningModalOpen}
              onClose={() => {
                if (!importLearningApplying) setImportLearningModalOpen(false);
              }}
              workspaceId={workspaceId}
              gradeId={effectiveGradeId}
              defaultGradeNumber={defaultGradeNumberForImport}
              termNumber={termNumber ?? 1}
              preferredTemplateName={decodedTrack}
              applying={importLearningApplying}
              onApply={handleApplyProgramLearningImport}
            />
            <ProgramProgressTable
              key={`${trackWorkspaceScopeId}::${programTableMountKey}`}
              rows={displayTableRows}
              workspaceId={workspaceId}
              academicYear={academicYear}
              termNumber={termNumber}
              focusAnnualCellKey={focusAnnualCellKeyFromUrl || null}
              programCellsByAnnualKey={displayProgramCellsByAnnualKey}
              syncMirrorIndicatorCellsByAnnualKey={syncMirrorIndicatorCellsByAnnualKey}
              syncMirrorView={isSyncMirrorView}
              syncMirrorRepAnnualCellKeyByMirrorKey={syncMirrorRepAnnualCellKeyByMirrorKey}
              syncMirrorMirrorAnnualCellKeysByRepKey={syncMirrorMirrorAnnualCellKeysByRepKey}
              saveMode="fixed_content"
              onUpsertFixedContentRows={upsertScopeContentRows}
              onBindSaveAction={handleBindSaveAction}
              onDirtyChange={setHasUnsavedChanges}
              workspaceScopeId={trackWorkspaceScopeId}
              slideDraftAllowedScopeIds={
                isSyncActive && representativeWorkspaceScopeId
                  ? [trackWorkspaceScopeId, representativeWorkspaceScopeId]
                  : undefined
              }
              onWorkspaceRowsChange={handleWorkspaceRowsChange}
              onEnsureWorkspaceSnapshot={handleEnsureWorkspaceSnapshot}
              readOnly={false}
              fixedModeProgramCellFields={fixedModeProgramCellFields}
              lockedFieldIds={lockedFieldIds}
              restrictMutatingActions={isSyncMirrorView}
              classScheduleSectionRows={selectedEntry?.sectionRows ?? []}
              registeredTimetableNames={registeredTimetableNames}
              registeredTimetableWildcardChars={registeredTimetableWildcardChars}
              scheduleSlotAnnualCellKeys={scheduleSlotAnnualCellKeys}
              programUndoRedoApiRef={programUndoRedoApiRef}
              onProgramUndoRedoAvailabilityChange={setProgramUndoRedoAvail}
              programDataCleanupGradeId={effectiveGradeId}
              programDataCleanupScopeType="track"
              programDataCleanupScopeKey={scopeKey}
              onProgramDataCleanupComplete={() => setSaveRefreshRev((v) => v + 1)}
            />
          </article>
        </>
      )}
      <button
        type="button"
        className="cp-fab-save"
        onClick={() => {
          void handleSave();
        }}
        disabled={!programSavePending}
        aria-label={programSavePending ? '프로그램 통합 저장하기' : '프로그램 변경사항 없음'}
        title={programSavePending ? '프로그램 통합 저장하기' : '프로그램 변경사항 없음'}
      >
        <img src="/Save.svg" alt="" className="cp-fab-save-icon" />
      </button>
      {saveToastState ? (
        <div className="cp-toast-auto" role="status" aria-live="polite">
          <div className={`cp-toast-auto-box cp-toast-auto-box-${saveToastState.kind}`}>
            {saveToastState.kind === 'saving' ? (
              <span className="cp-toast-auto-spinner" aria-hidden="true" />
            ) : saveToastState.kind === 'success' ? (
              <span className="cp-toast-auto-success-icon" aria-hidden="true">
                v
              </span>
            ) : null}
            <span>{saveToastState.message}</span>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default ProgramTrackPage;
